import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  TableRow,
  Typography,
} from "@material-ui/core";
import * as Yup from "yup";
import voca from "voca";

import useTabPanelStyles from "../../styles/TabPanel.style";
import useStyles from "./AdminUsersTabPanel.style";
import { InputField } from "../../../../components/input-field/InputField";
import { FormikHelpers, useFormik } from "formik";
import { IoIosMail } from "react-icons/io";
import { InputButton } from "../../../../components/input-button/InputButton";
import User from "../../../../models/User";
import TableData, {
  StyledTableCell,
  StyledTableRow,
} from "../../components/TableData";
import moment from "moment";
import ICIPAdminService from "../../../../services/ICIPAdminService";
import { UserAttributes } from "../../../../attributes/user-attributes";
import AdminRole from "../../../../models/AdminRole";
import { AdminRoleAttributes } from "../../../../attributes/admin-role-attributes";
import { InputSelect } from "../../../../components/input-select/InputSelect";

interface AdminUsersTabPanelProps {}

enum ScreenStatus {
  loading,
  failure,
  succeed,
  initial,
  adding,
}

interface LoadingState {
  status: ScreenStatus;
}

interface StateContent {
  users?: Array<User>;
  adminRoles?: Array<AdminRole>;
}

export const AdminUsersTabPanel = (props: AdminUsersTabPanelProps) => {
  const tabPanelClasses = useTabPanelStyles(props);
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [loadingState, setLoadingState] = useState(
    (): LoadingState => {
      return { status: ScreenStatus.initial };
    }
  );
  const [data, setData] = useState(
    (): StateContent => {
      return {
        users: [],
        adminRoles: [],
      };
    }
  );

  const formikValidation = (): any => {
    return Yup.object().shape({
      email: Yup.string()
        .email(intl.formatMessage({ id: "error.email" }))
        .required(intl.formatMessage({ id: "error.email" })),
      last_name: Yup.string().required(
        intl.formatMessage({ id: "error.enterName" })
      ),
      first_name: Yup.string().required(
        intl.formatMessage({ id: "error.enterName" })
      ),
      role_id: Yup.number().required(
        intl.formatMessage({ id: "error.selectRole" })
      ),
    });
  };

  const formikInitialValues = (): any => {
    return {
      email: "",
      last_name: "",
      first_name: "",
      role_id: "",
    };
  };

  const handleRoleSelection = (event: any, child: any) => {
    formik.setValues({ ...formik.values, role_id: event.target.value });
  };

  const formik = useFormik({
    initialValues: formikInitialValues(),
    validationSchema: formikValidation(),
    onSubmit: async (
      values: any,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response = await ICIPAdminService.addAdminUser(values);
        setSubmitting(false);

        if (response.isSuccess) {
          if (response.data.code === 150) {
            let errors = JSON.parse(JSON.stringify(response.data.data));
            setErrors({ ...errors });
          }

          if (response.data.code === 100) {
            const user: User = User.fromJson(
              JSON.parse(JSON.stringify(response.data.data))
            );
            let oldUsers = [...data.users];
            oldUsers.splice(0, 0, user);

            await formik.setValues({ ...formikInitialValues() });
            await formik.setErrors({});
            setData({ ...data, users: oldUsers });
          }

          if (response.data.code === 101) {
            setErrors({ unknown: response.data.message });
          }
          return;
        }
      } catch (err) {
        console.dir(err);
      }

      setErrors({
        unknown: intl.formatMessage({ id: "error.error" }),
      });
    },
  });

  const renderUsersTableRows = () => {
    return data.users.map((row, index) => {
      let user = row as User;

      return (
        <StyledTableRow key={index}>
          <StyledTableCell component="td" scope="row">
            {voca.titleCase(user.user_detail.last_name)}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {voca.titleCase(user.user_detail.first_name)}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {user.user_detail.email}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row" />
          <StyledTableCell component="td" scope="row">
            {user.admin_roles !== undefined && user.admin_roles !== null ? (
              user.admin_roles.map((item, index) => {
                return (
                  <Typography component="span" className={classes.roleItem}>
                    {item.role}
                  </Typography>
                );
              })
            ) : (
              <></>
            )}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row">
            {moment(user.created_at).format("DD, MMM yyyy @HH:mm")}
          </StyledTableCell>
          <StyledTableCell component="td" scope="row" />
        </StyledTableRow>
      );
    });
  };

  const renderUsersTableHeader = () => {
    return (
      <TableRow>
        <StyledTableCell>
          <FormattedMessage id="sharedScreen.lastName" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.firstName" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.email" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.telephone" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.role" />
        </StyledTableCell>
        <StyledTableCell align="left">
          <FormattedMessage id="sharedScreen.addedAt" />
        </StyledTableCell>
        <StyledTableCell align="right">
          <FormattedMessage id="sharedScreen.actions" />
        </StyledTableCell>
      </TableRow>
    );
  };

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    return "";
  };

  // Request

  const loadUsers = () => {
    setLoadingState({ ...loadingState, status: ScreenStatus.loading });

    const response = ICIPAdminService.allAdminUsers();
    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let users: Array<User> = [];
        let roles: Array<AdminRole> = [];

        JSON.parse(JSON.stringify(response.data.data)).users.forEach(
          (item, index) => {
            users.push(User.fromJson(item as UserAttributes));
          }
        );

        JSON.parse(JSON.stringify(response.data.data)).roles.forEach(
          (item, index) => {
            roles.push(AdminRole.fromJson(item as AdminRoleAttributes));
          }
        );

        setData({ users: users, adminRoles: roles });
        setLoadingState({ ...loadingState, status: ScreenStatus.succeed });
      } else {
        setLoadingState({ ...loadingState, status: ScreenStatus.failure });
      }
    });
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const buildRendering = (): any => {
    if (loadingState.status === ScreenStatus.failure) {
      return (
        <Box
          component="div"
          className={classes.contentWrapper}
          mt={2}
          style={{ marginLeft: "12px" }}
        >
          <Typography variant="h6" component="h6" color="error">
            <FormattedMessage id="error.error" />
          </Typography>

          <Box component="div" mt={2}>
            <InputButton
              color="secondary"
              size="medium"
              variant="contained"
              type="button"
              onClick={(_) => loadUsers()}
            >
              <FormattedMessage id="sharedScreen.retry" />
            </InputButton>
          </Box>
        </Box>
      );
    }

    if (loadingState.status === ScreenStatus.succeed) {
      return (
        <Grid component="div" className={classes.contentWrapper}>
          <Grid container>
            <Grid item sm={3}>
              <Box component="div" className={classes.leftHeader} pl={1}>
                <Typography variant="h3" component="h3">
                  <FormattedMessage id="sharedScreen.users" />
                </Typography>
              </Box>
              <Box component="div" className={classes.formWrapper} ml={1}>
                <form
                  noValidate
                  autoComplete="off"
                  onSubmit={formik.handleSubmit}
                >
                  <Box
                    component="div"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Box mr={1} style={{ width: "100%" }}>
                      <InputField
                        fullWidth
                        id="last_name"
                        name="last_name"
                        label={intl.formatMessage({
                          id: "sharedScreen.lastName",
                        })}
                        placeholder={intl.formatMessage({
                          id: "sharedScreen.enterLastName",
                        })}
                        type="text"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        error={
                          Boolean(formik.touched.last_name) &&
                          Boolean(formik.errors.last_name)
                        }
                        helperText={
                          formik.touched.last_name && formik.errors.last_name
                        }
                      />
                    </Box>
                    <Box ml={1} style={{ width: "100%" }}>
                      <InputField
                        fullWidth
                        id="first_name"
                        name="first_name"
                        label={intl.formatMessage({
                          id: "sharedScreen.firstName",
                        })}
                        placeholder={intl.formatMessage({
                          id: "sharedScreen.enterFirstName",
                        })}
                        type="text"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        error={
                          Boolean(formik.touched.first_name) &&
                          Boolean(formik.errors.first_name)
                        }
                        helperText={
                          formik.touched.first_name && formik.errors.first_name
                        }
                      />
                    </Box>
                  </Box>
                  <Box component="div" mt={2}>
                    <InputField
                      fullWidth
                      id="email"
                      name="email"
                      label={intl.formatMessage({ id: "sharedScreen.email" })}
                      placeholder={intl.formatMessage({
                        id: "sharedScreen.enterEmail",
                      })}
                      type="text"
                      endAdornment={
                        <InputAdornment position="end">
                          <IoIosMail />
                        </InputAdornment>
                      }
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={
                        Boolean(formik.touched.email) &&
                        Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Box>
                  <Box component="div" mt={2}>
                    <InputSelect
                      fullWidth
                      id="role_id"
                      name="role_id"
                      value={formik.values.role_id}
                      label={intl.formatMessage({
                        id: "sharedScreen.adminRole",
                      })}
                      onChange={handleRoleSelection}
                      error={
                        Boolean(formik.touched.role_id) &&
                        Boolean(formik.errors.role_id)
                      }
                      helperText={
                        formik.touched.role_id && formik.errors.role_id
                      }
                    >
                      <MenuItem value="" disabled>
                        <FormattedMessage id="sharedScreen.selectRole" />
                      </MenuItem>
                      {data.adminRoles.map((item) => {
                        return (
                          <MenuItem value={item.id}>
                            {voca.titleCase(item.role)}
                          </MenuItem>
                        );
                      })}
                    </InputSelect>
                  </Box>
                  <Box
                    mt={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {formik.errors ? (
                      <Typography
                        variant="h6"
                        component="h6"
                        color="error"
                        style={{ paddingRight: "14px" }}
                      >
                        {genericError()}
                      </Typography>
                    ) : (
                      <></>
                    )}
                    {formik.isSubmitting ? (
                      <Box
                        component="span"
                        mr={4}
                        mt={1}
                        display="inline-block"
                      >
                        <CircularProgress
                          color="secondary"
                          disableShrink={false}
                          size={20}
                          thickness={4}
                        />
                      </Box>
                    ) : (
                      <></>
                    )}
                    <Box width={100}>
                      <InputButton
                        color="secondary"
                        fullWidth
                        size="medium"
                        variant="contained"
                        type="submit"
                        disabled={formik.isSubmitting}
                      >
                        <FormattedMessage id="sharedScreen.create" />
                      </InputButton>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Grid>
            <Grid item sm={9}>
              <Box component="div" className={classes.leftHeader} pl={8}>
                <Typography variant="h3" component="h3">
                  <FormattedMessage id="sharedScreen.allUsers" />
                </Typography>
              </Box>
              <Box
                component="div"
                className={classes.tableWrapper}
                pl={8}
                pr={2}
              >
                <TableData
                  rows={renderUsersTableRows()}
                  header={renderUsersTableHeader()}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      );
    }

    return (
      <Box component="span" style={{ display: "block", padding: "20px 32px" }}>
        <CircularProgress
          color="secondary"
          disableShrink={false}
          size={20}
          thickness={4}
        />
      </Box>
    );
  };

  return (
    <Box component="div" className={tabPanelClasses.root}>
      {buildRendering()}
    </Box>
  );
};